import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { ApiError, call, onSessionChange, token, type Availability, type Me, type Slab } from '../api';
import { listVersion, onListChange, readList } from '../phone-list';
import { Link } from '../router';
import { GRADER_COLORS, LOGO_GRADERS } from '../words';

// The parts every GrailFind page shares, as the server-rendered pages draw them (api/lib/grail_web/grailfind_page.ex).

/**
 * The bottom of the screen, where the website's bars sit one above the other, so none covers another (redteam #261
 * N2: the old list's question covered Undo). App gives the place; a page's bar goes there through BottomBar.
 */
export const BottomPlace = createContext<HTMLElement | null>(null);

/** A page's bar at the bottom of the screen (Undo, the card page's Add bar), above the old list's question. */
export function BottomBar({ children }: { children: ReactNode }) {
  const place = useContext(BottomPlace);
  return place ? createPortal(children, place) : <>{children}</>;
}

/** GrailTab's logo, exactly as the app icon draws it: the black card fan on the yellow tile. */
export function Mark() {
  return (
    <svg viewBox="0 0 100 100" aria-hidden="true">
      <rect width="100" height="100" rx="22.5" fill="#F5C518" />
      <g transform="translate(3.8 -1.5) scale(0.95)">
        <g transform="translate(7 1)">
          <path d="M34 26 H58 Q62 26 62 30 V70 Q62 74 58 74 H34 Q30 74 30 70 V30 Q30 26 34 26 Z" fill="#121417" stroke="#F5C518" strokeWidth="3.2" strokeLinejoin="round" transform="rotate(-40 32 78)" />
          <path d="M34 26 H58 Q62 26 62 30 V70 Q62 74 58 74 H34 Q30 74 30 70 V30 Q30 26 34 26 Z" fill="#121417" stroke="#F5C518" strokeWidth="3.2" strokeLinejoin="round" transform="rotate(-20 32 78)" />
          <path d="M46 25 H61.8 L64.8 20 H74 Q78 20 78 24 V74 Q78 78 74 78 H46 Q42 78 42 74 V29 Q42 25 46 25 Z" fill="#121417" stroke="#F5C518" strokeWidth="3.2" strokeLinejoin="round" />
        </g>
      </g>
    </svg>
  );
}

const ScanIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2" />
    <path d="M8 9h3v3H8zM13 13h3v3h-3zM13 9h3M8 15h3" />
  </svg>
);

export const ShopIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 10v9h16v-9" />
    <path d="M3 5h18l-1 5H4z" />
    <path d="M10 19v-5h4v5" />
  </svg>
);

/**
 * The bar at the top of every page: the logo, which always goes to the shows, the Scan button, and My list with its
 * count (or the page's own button in `right`).
 */
export function Top({ right }: { right?: ReactNode }) {
  const count = useListCount();
  return (
    <header className="top">
      <Link className="brand" href="/shows" aria-label="GrailFind, all shows">
        <Mark />
        <span>
          GrailFind<small>by GrailTab</small>
        </span>
      </Link>
      <span className="top-r">
        <Link className="scan-go" href="/scan" aria-label="Scan a sticker">
          <ScanIcon />
        </Link>
        {right ?? (
          <Link className="mylist" href="/list">
            My list{count ? <b>{count}</b> : null}
          </Link>
        )}
      </span>
    </header>
  );
}

/**
 * The way back to the page above, over the page's title (José, 23 Oct 2026: "I need to be able to go back"). A plain
 * link, never the browser's history: a buyer who opened the page from a QR code has no page before it.
 */
export function Back({ href, label }: { href: string; label: string }) {
  return (
    <Link className="back" href={href}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M15 5l-7 7 7 7" />
      </svg>
      <span>{label}</span>
    </Link>
  );
}

/**
 * A card picture, or the striped card back when it has none or it does not load (no signal at the show). The API
 * allows an http:// picture; the site asks for it over https://, as a browser does on an https page, so the picture
 * policy names no http: (redteam #246 N1).
 */
export function Picture({ src, alt = '', className }: { src: string | null; alt?: string; className?: string }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed || !/^https?:\/\//i.test(src)) return <span className="none" aria-hidden="true" />;
  return <img className={className} src={src.replace(/^http:\/\//i, 'https://')} alt={alt} loading="lazy" onError={() => setFailed(true)} />;
}

/** A card's picture as a link to its page; a slab of a grader the site has a logo for shows as a slab. */
export function Thumb({ id, name, picture, slab }: { id: string; name: string; picture: string | null; slab: Slab | null }) {
  const href = `/i/${id}`;
  if (slab && LOGO_GRADERS.includes(slab.grader))
    return (
      <Link className="thumb slabbed" href={href} aria-label={`Open ${name}, ${slab.grader} ${slab.grade}`}>
        <span className="tl" style={{ borderBottomColor: GRADER_COLORS[slab.grader] ?? '#15171A' }}>
          <img src={`/graders/${slab.grader.toLowerCase()}.png`} alt={slab.grader} />
          <b>{slab.grade}</b>
        </span>
        <span className="ti">
          <Picture src={picture} />
        </span>
      </Link>
    );
  return (
    <Link className="thumb" href={href} aria-label={`Open ${name}`}>
      <Picture src={picture} />
    </Link>
  );
}

export const Spinner = () => <span className="spin" role="status" aria-label="Loading" />;

/** What went wrong, in plain words, with Try again. */
export function Failed({ error, retry }: { error: unknown; retry?: () => void }) {
  const offline = error instanceof ApiError && error.status === 0;
  return (
    <div className="err" role="alert">
      <p style={{ margin: 0 }}>{offline ? 'No signal. Check your connection and try again.' : 'This page did not load. Try again in a moment.'}</p>
      {retry ? (
        <button className="link" type="button" onClick={retry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

/** A call's answer for a page: `data` once it came, `error` if it failed, and `reload`. A new `again` calls again. */
export function useCall<T>(path: string | null, again: unknown = null) {
  const session = useSyncExternalStore(onSessionChange, token, () => null);
  // Identity only; no token is put in a rendered value or in the retained response state.
  const scope = useMemo(() => ({}), [session]);
  const [state, setState] = useState<{ path: string | null; scope?: object; data?: T; error?: unknown; loading?: boolean }>({ path: null });
  const [round, setRound] = useState(0);

  useEffect(() => {
    if (!path) return;
    let live = true;
    setState((previous) => ({ path, scope, data: previous.path === path && previous.scope === scope ? previous.data : undefined, loading: true }));
    call<T>(path, { cache: round > 0 ? 'no-store' : undefined }).then(
      (data) => live && setState({ path, scope, data, loading: false }),
      (error) => {
        if (!live) return;
        const transient = error instanceof ApiError && (error.status === 0 || error.status === 429 || error.status >= 500);
        setState((previous) => ({ path, scope, data: transient && previous.path === path && previous.scope === scope ? previous.data : undefined, error, loading: false }));
      },
    );
    return () => {
      live = false;
    };
  }, [path, round, scope, again]);

  const reload = useCallback(() => setRound((r) => r + 1), []);
  const current = state.path === path && state.scope === scope;
  return { data: current ? state.data : undefined, error: current ? state.error : undefined, loading: !!path && (!current || !!state.loading), reload };
}

export function availabilityText(state?: Availability): string {
  return state === 'available' ? 'Available at last check' : state === 'held' ? 'Currently held' : state === 'sold' ? 'Sold' : 'Availability not checked';
}

/** A dated server snapshot and an explicit refresh; a failed refresh keeps its old time visible. */
export function StockCheck({ availability, checkedAt, refresh, loading, error }: { availability?: Availability; checkedAt?: string; refresh: () => void; loading: boolean; error?: unknown }) {
  const date = typeof checkedAt === 'string' && checkedAt.length < 100 ? new Date(checkedAt) : null;
  const when = date && Number.isFinite(date.getTime()) ? date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;
  return (
    <section className="stock-check" aria-label="Stock availability">
      <div aria-live="polite">
        <b>{availability ? availabilityText(availability) : when ? 'Stock snapshot' : 'Availability not checked'}</b>
        <p>{when ? `Checked ${when}` : 'Snapshot time unavailable.'}</p>
        {error ? <p role="alert">{error instanceof ApiError && error.status === 0 ? 'No signal. This snapshot may be old.' : 'Could not refresh. The previous snapshot is shown.'}</p> : null}
      </div>
      <button className="clear" type="button" onClick={refresh} disabled={loading}>{loading ? 'Checking…' : 'Refresh stock'}</button>
    </section>
  );
}

/** Whether a buyer is signed in here (a session token is kept). */
export function useSignedIn(): boolean {
  return useSyncExternalStore(onSessionChange, () => token() !== null, () => false);
}

/** The signed-in buyer, or null; undefined while it loads. */
export function useMe(): Me | null | undefined {
  const signedIn = useSignedIn();
  const changed = useSyncExternalStore(onListChange, listVersion, () => 0);
  const { data, error } = useCall<Me>(signedIn ? '/api/find/me' : null, changed);
  if (!signedIn) return null;
  if (error) return null;
  return data;
}

/** My list's count for the header: the account's when signed in, else the phone's. */
export function useListCount(): number {
  const me = useMe();
  const phone = useSyncExternalStore(onListChange, () => readList().length, () => 0);
  return me ? me.count : phone;
}
